import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";

import { DOWN, UP } from "@hermes/utils-generic/constants";
import { stopEventPropagation } from "@hermes/utils-generic/helpers";

@Component({
    selector: "h-image-zoom-scale-slider-deprecated",
    templateUrl: "./image-zoom-scale-slider-deprecated.component.html",
    styleUrls: ["./image-zoom-scale-slider-deprecated.component.scss"],
})
/**
 * this component is used to display the slider component of the zoom
 */
export class ImageZoomScaleSliderDeprecatedComponent
    implements OnInit, AfterContentInit, OnChanges
{
    /**
     * scale of zoom
     */
    @Input()
    public scale: number = 1;

    /**
     * when scale change, we send event to parent component
     */
    @Output()
    public scaleChange: EventEmitter<number> = new EventEmitter<number>();

    /**
     * thumb content
     */
    @ViewChild("thumb", { static: true })
    private thumbElement!: ElementRef<HTMLElement>;

    /**
     * rail content
     */
    @ViewChild("rail", { static: true })
    private railElement!: ElementRef<HTMLElement>;

    public minScale: number = 1;

    public maxScale: number = 2;

    /**
     * position of thumb in rail
     */
    public positionTop!: number;

    /**
     * scale formatted (scale * 100 %)
     */
    public currentScale: string = "100";

    /**
     * scaleLevels : when user zoom using plus/minus button
     */
    public scaleLevels: number[] = [1, 1.3, 1.7, 2];

    public currentScaleIndex: number = 0;

    /**
     * sliderHeight - thumbHeight
     */
    private sliderHeight: number = 203.5;

    private isDragging: boolean = false;
    private shiftY?: number;

    private thumb!: HTMLElement;

    private rail!: HTMLElement;

    public ngOnInit(): void {
        this.positionTop = this.sliderHeight;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.scale && changes.scale.currentValue === 1) {
            this.positionTop = this.sliderHeight;
        }
    }

    public ngAfterContentInit(): void {
        this.thumb = this.thumbElement.nativeElement;
        this.rail = this.railElement.nativeElement;
    }

    /**
     * when click in plus button, we increment  zoom scale
     */
    public incrementScale(): void {
        if (this.currentScaleIndex === 3) {
            return;
        }
        this.currentScaleIndex = Math.min(this.currentScaleIndex + 1, 3);
        this.scale = this.scaleLevels[this.currentScaleIndex];
        this.currentScale = this.formatValue(this.scale);
        this.scaleChange.emit(this.scale);
        this.setPositionTop();
    }

    /**
     * when click on  minus button, we decrement zoom scale
     */
    public decrementScale(): void {
        if (this.currentScaleIndex === 0) {
            return;
        }
        this.currentScaleIndex = Math.max(this.currentScaleIndex - 1, 0);
        this.scale = this.scaleLevels[this.currentScaleIndex];
        this.currentScale = this.formatValue(this.scale);
        this.scaleChange.emit(this.scale);
        this.setPositionTop();
    }

    /**
     * handle click on slider
     */
    public onMouseDown(event: MouseEvent): void {
        stopEventPropagation(event); // prevent selection start (browser action)
        this.shiftY = event.clientY - this.thumb.getBoundingClientRect().top;
        this.isDragging = true;
    }

    /**
     * handle drag in drop on slider
     */
    public onMouseMove(event: MouseEvent): void {
        if (this.isDragging && this.shiftY !== undefined) {
            let newTop =
                event.clientY -
                this.shiftY -
                this.rail.getBoundingClientRect().top;

            // the pointer is out of rail => lock the thumb within the bounaries
            if (newTop < 0) {
                newTop = 0;
            }
            const topEdge = this.rail.offsetHeight - this.thumb.offsetHeight;

            if (newTop > topEdge) {
                newTop = topEdge;
            }
            this.positionTop = newTop;

            this.scale = 1 + (1 - newTop / this.sliderHeight);
            this.scale = Math.round(this.scale * 100) / 100;

            this.currentScale = this.formatValue(this.scale);
            this.scaleChange.emit(this.scale);
        }
    }

    public onMouseUp(): void {
        this.currentScaleIndex = this.findCurrentIndex();
        this.isDragging = false;
    }

    /**
     * Handle zoom with KeyboardEvent event
     */
    public onKeyDown(event: KeyboardEvent): void {
        event.stopPropagation();
        switch (event.key) {
            case DOWN: {
                this.decrementScale();
                break;
            }
            case UP: {
                this.incrementScale();
                break;
            }
            default: {
                break;
            }
        }
    }

    public formatValue(scale: number): string {
        return `${Math.floor(scale * 100)}`;
    }

    private setPositionTop(): void {
        this.positionTop =
            this.sliderHeight -
            (this.scaleLevels[this.currentScaleIndex] - 1) * this.sliderHeight;
    }

    /**
     * when we combine the use of plus / minus and the drag and drop, we need to check the current index
     */
    private findCurrentIndex(): number {
        const fakeScaleLevels = [...this.scaleLevels, this.scale].sort(
            (a, b) => {
                if (a < b) {
                    return -1;
                }
                if (a > b) {
                    return 1;
                }
                return 0;
            },
        );
        return fakeScaleLevels.indexOf(this.scale);
    }
}
